import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '../services/supabase';
import { registerStylist } from '../services/api';

import './StylistSignupPage.scss';
import { toast } from 'react-toastify';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
} from '@mui/material';
import LanguageDropdown from '../components/LanguageDropdown';
import { validatePhoneNumber, getPhoneNumberError } from '../utils/validations';
import { formatPhoneWithCountryCode } from '../utils/phoneUtils';

interface TranslationSet {
  completeSignup: string;
  invalidEmailLink: string;
  noUserFound: string;
  accountCreated: string;
  passwordUpdated: string;
  completeSignupButton: string;
  setPassword: string;
  createPassword: string;
  confirmPassword: string;
  confirmPasswordPlaceholder: string;
  passwordMismatch: string;
  phoneNumber: string;
  enterPhoneNumber: string;
  sendVerificationCode: string;
  verificationCode: string;
  enterVerificationCode: string;
  verifyCode: string;
  verificationCodeSent: string;
  failedToSendVerificationCode: string;
  phoneNumberVerified: string;
  failedToVerifyCode: string;
  noConfirmationResult: string;
}

interface TranslationsMap {
  zh: TranslationSet;
  en: TranslationSet;
  [key: string]: TranslationSet;
}

interface SignupFormData {
  password: string;
  confirmPassword: string;
}

function SignupPage(): React.ReactElement {
  const translations: TranslationsMap = {
    zh: {
      completeSignup: '完成註冊',
      invalidEmailLink: '無效的電子郵件鏈接',
      noUserFound: '未找到用戶',
      accountCreated: '帳戶已創建',
      passwordUpdated: '密碼已更新',
      completeSignupButton: '完成註冊',
      setPassword: '設置密碼',
      createPassword: '創建密碼',
      confirmPassword: '確認密碼',
      confirmPasswordPlaceholder: '請再次輸入密碼',
      passwordMismatch: '密碼不匹配',
      phoneNumber: '電話號碼',
      enterPhoneNumber: '輸入您的電話號碼',
      sendVerificationCode: '發送驗證碼',
      verificationCode: '驗證碼',
      enterVerificationCode: '輸入驗證碼',
      verifyCode: '驗證',
      verificationCodeSent: '驗證碼已發送',
      failedToSendVerificationCode: '發送驗證碼失敗',
      phoneNumberVerified: '電話號碼已驗證',
      failedToVerifyCode: '驗證碼驗證失敗',
      noConfirmationResult: '沒有可用的確認結果'
    },
    en: {
      completeSignup: 'Complete Signup',
      invalidEmailLink: 'Invalid email link',
      noUserFound: 'No user found',
      accountCreated: 'Account created',
      passwordUpdated: 'Password updated',
      completeSignupButton: 'Complete Signup',
      setPassword: 'Set Password',
      createPassword: 'Create Password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Please enter your password again',
      passwordMismatch: 'Passwords do not match',
      phoneNumber: 'Phone Number',
      enterPhoneNumber: 'Enter your phone number',
      sendVerificationCode: 'Send Verification Code',
      verificationCode: 'Verification Code',
      enterVerificationCode: 'Enter verification code',
      verifyCode: 'Verify Code',
      verificationCodeSent: 'Verification code sent',
      failedToSendVerificationCode: 'Failed to send verification code',
      phoneNumberVerified: 'Phone number verified',
      failedToVerifyCode: 'Failed to verify code',
      noConfirmationResult: 'No confirmation result available'
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState<string>('en'); // Default language
  const t = translations[currentLanguage];

  const { handleSubmit } = useForm<SignupFormData>({
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const [email, setEmail] = useState<string>('');
  const [businessId, setBusinessId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');

  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [userPassword, setUserPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');


  useEffect(() => {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);

    const emailFromUrl = params.get('email');
    const businessIdFromUrl = params.get('businessId');

    console.log('Extracted email:', emailFromUrl);
    console.log('Extracted businessId:', businessIdFromUrl);

    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(1));

    console.log('Hash params:', hashParams);

    const accessToken = hashParams.get('access_token');

    if (accessToken) {
      supabase.auth.getSession()
        .then(({ data, error }) => {
          if (error) {
            console.error('Error verifying token:', error);
            toast.error(t.failedToVerifyCode);
          } else {
            console.log('Token verified successfully:', data);
          }
        })
        .catch((error: unknown) => {
          console.error('Error during token verification:', error);
          toast.error(t.failedToVerifyCode);
        });
    } else {
      toast.error(t.invalidEmailLink);
    }

    if (businessIdFromUrl) {
      setBusinessId(businessIdFromUrl);
    } else {
      toast.error(t.invalidEmailLink);
    }

    if (emailFromUrl) {
      setEmail(emailFromUrl);
    } else {
      toast.error(t.invalidEmailLink);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Using centralized phone formatting utility from phoneUtils

  const handlePhoneAuth = async (phoneNumber: string): Promise<unknown> => {

    try {
      const { error } = await supabase.auth.updateUser({
        phone: formatPhoneWithCountryCode(phoneNumber)
      });
      if (error) throw error;
    } catch (error: unknown) {
      console.error('Error updating user with phone number:', error);
      throw error;
    }

    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: formatPhoneWithCountryCode(phoneNumber)
      });
      if (error) throw error;
      return data;
    } catch (error: unknown) {
      console.error('Error during phone authentication:', error);
      throw error;
    }
  };

  const sendVerificationCode = async (): Promise<void> => {
    if (!validatePhoneNumber(phoneNumber)) {
      toast.error(getPhoneNumberError(phoneNumber));
      return;
    }

    setLoading(true);
    const phoneNumberWithCountryCode = formatPhoneWithCountryCode(phoneNumber);

    try {
      await handlePhoneAuth(phoneNumberWithCountryCode);
      setIsCodeSent(true);
      toast.success('Verification code sent successfully!');
    } catch (error: unknown) {
      console.error('Error sending verification code:', error);
      toast.error('Error sending verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (): Promise<void> => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.verifyOtp({
        phone: formatPhoneWithCountryCode(phoneNumber),
        token: verificationCode,
        type: 'sms'
      });

      if (error) throw error;

      setIsPhoneVerified(true);
      toast.success('Phone number verified successfully!');
    } catch (error: unknown) {
      console.error('Error verifying code:', error);
      toast.error('Error verifying code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (): Promise<void> => {
    setPasswordError('');
    if (!userPassword) {
      setPasswordError('Password is required');
      toast.error('Password is required');
      return;
    }
    if (userPassword !== confirmPassword) {
      setPasswordError(t.passwordMismatch);
      toast.error(t.passwordMismatch);
      return;
    }

    if (!isPhoneVerified) {
      toast.error('Please verify your phone number first');
      return;
    }

    try {
      setLoading(true);

      // Set password for the user
      const { error: updateError } = await supabase.auth.updateUser({
        password: userPassword
      });

      if (updateError) throw updateError;

      // Sign in with email and password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email,
        password: userPassword
      });

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      if (signInError) throw signInError;

      // Register stylist with our backend
      await registerStylist({
        email: email,
        businessId: businessId,
        phone: formatPhoneWithCountryCode(phoneNumber),
        token: sessionData.session!.access_token
      });

      toast.success(t.accountCreated);

      // Redirect to dashboard
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error: unknown) {
      console.error('Error during signup:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during sign up';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{
        mt: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Box sx={{alignSelf: 'flex-end', mb: 2}}>
          <LanguageDropdown currentLanguage={currentLanguage} changeLanguage={setCurrentLanguage} />
        </Box>
        <Typography component="h1" variant="h4" gutterBottom>
          {t.completeSignup}
        </Typography>
        <Box component="form" sx={{ mt: 3, width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            margin="normal"
            label={t.phoneNumber}
            value={phoneNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              setPhoneNumber(value);
              if (value && !validatePhoneNumber(value)) {
                setPhoneError('Invalid phone number format.');
              } else {
                setPhoneError('');
              }
            }}
            placeholder="Enter your phone number"
            error={!!phoneError}
            helperText={phoneError}
          />
          <div id="recaptcha-container"></div>
          {!isCodeSent ? (
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 1, mb: 2 }}
              onClick={sendVerificationCode}
              disabled={loading}
            >
              {t.sendVerificationCode}
            </Button>
          ) : !isPhoneVerified ? (
            <>
              <TextField
                fullWidth
                margin="normal"
                label={t.verificationCode}
                value={verificationCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value)}
                placeholder={t.enterVerificationCode}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
                onClick={verifyCode}
                disabled={loading}
              >
                {t.verifyCode}
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                margin="normal"
                label={t.setPassword}
                type="password"
                value={userPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserPassword(e.target.value)}
              />
              <TextField
                fullWidth
                margin="normal"
                label={t.confirmPassword}
                type="password"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                placeholder={t.confirmPasswordPlaceholder}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
                type="submit"
                disabled={loading || !isPhoneVerified}
              >
                {t.completeSignupButton}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default SignupPage;
